import { useEffect } from "react";
import { UseFormReturn, useWatch } from "react-hook-form";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CURRENCIES } from "@/helpers/currency-map";
import { BundleWizardData } from "./wizard-form";
import SessionTypesField from "./session-types-field";

export default function StepPricing({
  form,
}: {
  form: UseFormReturn<BundleWizardData>;
}) {
  const inDays = useWatch({
    control: form.control,
    name: "is_expiration_in_days",
  });

  // Auto-suggest the reschedule fee as 50% of the per-session amount
  // (amount ÷ session count), until the admin edits the field themselves.
  const amount = useWatch({ control: form.control, name: "amount" });
  const sessionTypes = useWatch({ control: form.control, name: "session_types" });
  const sessionQuantity = useWatch({
    control: form.control,
    name: "session_quantity",
  });
  useEffect(() => {
    if (form.getFieldState("reschedule_amount").isDirty) return;
    const count = sessionTypes?.length || sessionQuantity || 1;
    const suggestion = Math.round((0.5 * (amount || 0)) / count);
    form.setValue("reschedule_amount", suggestion);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, sessionTypes, sessionQuantity]);

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="currency"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Currency</FormLabel>
              <Select value={field.value} onValueChange={field.onChange}>
                <FormControl>
                  <SelectTrigger>
                    <SelectValue placeholder="Currency">
                      {field.value}
                    </SelectValue>
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c.value} value={c.value}>
                      {c.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Amount <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Amount"
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="reschedule_amount"
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                Reschedule Amount <span className="text-red-500">*</span>
              </FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Reschedule amount"
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  value={field.value || ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="total_allowed_user"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Total Allowed Users</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Maximum users"
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <SessionTypesField form={form} />

      <FormField
        control={form.control}
        name="is_expiration_in_days"
        render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
            <FormLabel className="text-base">Expiration in Days</FormLabel>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={(checked) => {
                  field.onChange(checked);
                  if (checked) {
                    form.setValue("expires_in_month", null);
                  } else {
                    form.setValue("expires_in_day", null);
                  }
                }}
              />
            </FormControl>
          </FormItem>
        )}
      />

      <div className="grid grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="expires_in_day"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expires in Days</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Number of days"
                  disabled={!inDays}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name="expires_in_month"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Expires in Months</FormLabel>
              <FormControl>
                <Input
                  type="number"
                  placeholder="Number of months"
                  disabled={inDays}
                  onChange={(e) =>
                    field.onChange(
                      e.target.value ? Number(e.target.value) : null,
                    )
                  }
                  value={field.value ?? ""}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>
    </div>
  );
}
